import React from 'react';
import { SubscriptionPlan } from '../../../services/subscriptionPlanService';
import { Edit2, Loader, Power, Check, Zap } from 'lucide-react';

interface SubscriptionPlansTableProps {
  plans: any[];
  isLoading: boolean;
  error?: string;
  onEdit?: (plan: SubscriptionPlan) => void;
  onToggleStatus?: (planId: string, currentStatus: boolean) => void;
  isSubmitting: boolean;
  isReadOnly?: boolean;
}

const SubscriptionPlansTable: React.FC<SubscriptionPlansTableProps> = ({
  plans,
  isLoading,
  error,
  onEdit,
  onToggleStatus,
  isSubmitting,
  isReadOnly = false,
}) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getIntervalLabel = (plan: any) => {
    // Handle admin plans
    if (plan.interval) {
      return plan.interval === 'monthly' ? '/month' : '/year';
    }
    // Handle existing plans
    if (plan.duration) {
      return plan.duration === 1 ? '/month' : `/year`;
    }
    return '';
  };

  const getPlanName = (plan: any) => {
    return plan.name || plan.tier || 'Unknown Plan';
  };

  const getPlanDescription = (plan: any) => {
    return plan.description || plan.slug || '';
  };

  const getPlanPrice = (plan: any) => {
    return plan.price || 0;
  };

  const getPlanFeatures = (plan: any) => {
    if (plan.features && Array.isArray(plan.features)) {
      return plan.features;
    }
    if (plan.entitlements) {
      return Object.keys(plan.entitlements).filter(key => plan.entitlements[key] === true);
    }
    return [];
  };

  const isActive = (plan: any) => {
    return plan.isActive !== false;
  };

  const isPlanCustom = (plan: any) => {
    // Custom plans have planType='custom'
    // System plans have planType='system' or no planType field
    return plan.planType === 'custom';
  };

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader size={40} className="text-[#23F47C] animate-spin" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <p className="text-red-400 text-lg font-medium">Failed to load plans</p>
          <p className="text-red-300 text-sm mt-1">{error}</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (plans.length === 0) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <Zap size={48} className="text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 text-lg font-medium">No subscription plans yet</p>
          <p className="text-gray-500 text-sm mt-1">Create your first plan to get started</p>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
      {plans.map((plan, idx) => {
        const planKey = plan.id || plan._id || plan.slug || `plan-${idx}`;
        return (
          <div
            key={planKey}
            className={`relative group rounded-xl border transition-all duration-300 overflow-hidden ${
              isActive(plan)
                ? 'border-[#23F47C]/30 bg-gradient-to-br from-[#16251D] to-[#0f1a15] hover:border-[#23F47C]/60 hover:shadow-lg hover:shadow-[#23F47C]/10'
                : 'border-gray-700/50 bg-gradient-to-br from-gray-900/50 to-gray-800/30 hover:border-gray-600'
            }`}
          >
            {/* Status Badge */}
            {!isReadOnly && (
              <div className="absolute top-4 right-4 z-10 flex flex-col gap-2 items-end">
                {isPlanCustom(plan) && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-[#23F47C]/20 text-[#23F47C] border border-[#23F47C]/30">
                    Custom
                  </span>
                )}
                <span
                  className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${
                    isActive(plan)
                      ? 'bg-[#23F47C]/20 text-[#23F47C] border border-[#23F47C]/30'
                      : 'bg-gray-700/50 text-gray-400 border border-gray-600/30'
                  }`}
                >
                  <span className={`w-2 h-2 rounded-full ${isActive(plan) ? 'bg-[#23F47C]' : 'bg-gray-500'}`} />
                  {isActive(plan) ? 'Active' : 'Inactive'}
                </span>
              </div>
            )}

            {/* Card Content */}
            <div className="p-6">
              {/* Plan Name */}
              <h3 className="text-xl font-bold text-white mb-2 pr-24">{getPlanName(plan)}</h3>

              {/* Description */}
              {getPlanDescription(plan) && (
                <p className="text-gray-400 text-sm mb-4 line-clamp-2">{getPlanDescription(plan)}</p>
              )}

              {/* Price Section */}
              <div className="mb-6 py-4 border-y border-gray-700/50">
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-[#23F47C]">
                    {formatCurrency(getPlanPrice(plan)).split('.')[0]}
                  </span>
                  <span className="text-gray-400 text-sm">
                    {formatCurrency(getPlanPrice(plan)).includes('.') ? '.' + formatCurrency(getPlanPrice(plan)).split('.')[1] : ''}
                  </span>
                  <span className="text-gray-500 text-sm ml-1">{getIntervalLabel(plan)}</span>
                </div>
              </div>

              {/* Features List */}
              {getPlanFeatures(plan).length > 0 && (
                <div className="mb-6">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Features</p>
                  <ul className="space-y-2">
                    {getPlanFeatures(plan).slice(0, 4).map((feature: string | Record<string, any>, featureIdx: number) => (
                      <li key={`${planKey}-feature-${featureIdx}`} className="flex items-start gap-2 text-sm text-gray-300">
                        <Check size={16} className="text-[#23F47C] mt-0.5 flex-shrink-0" />
                        <span>{typeof feature === 'string' ? feature : Object.keys(feature)[0]}</span>
                      </li>
                    ))}
                    {getPlanFeatures(plan).length > 4 && (
                      <li className="text-xs text-gray-500 italic pt-1">
                        +{getPlanFeatures(plan).length - 4} more features
                      </li>
                    )}
                  </ul>
                </div>
              )}

              {/* Actions */}
              {!isReadOnly && (
                <div className="flex gap-2 pt-4 border-t border-gray-700/50">
                  {/* Edit Button */}
                  <button
                    onClick={() => onEdit?.(plan)}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-[#23F47C]/10 text-[#23F47C] hover:bg-[#23F47C]/20 transition-colors font-medium text-sm border border-[#23F47C]/30 hover:border-[#23F47C]/60"
                    title="Edit plan"
                  >
                    <Edit2 size={16} />
                    Edit
                  </button>

                  {/* Toggle Status Button - Only for custom plans */}
                  {isPlanCustom(plan) && (
                    <button
                      onClick={() => onToggleStatus?.(plan.id || plan._id, isActive(plan))}
                      disabled={isSubmitting}
                      className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors border ${
                        isActive(plan)
                          ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20 border-red-500/30 hover:border-red-500/60'
                          : 'bg-green-500/10 text-green-400 hover:bg-green-500/20 border-green-500/30 hover:border-green-500/60'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                      title={isActive(plan) ? 'Deactivate plan' : 'Activate plan'}
                    >
                      <Power size={16} />
                      {isActive(plan) ? 'Deactivate' : 'Activate'}
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Hover Accent */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#23F47C]/0 via-[#23F47C]/0 to-[#23F47C]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
          </div>
        );
      })}
    </div>
  );
};

export default SubscriptionPlansTable;
